import { useMemo } from 'react';

import { Study, StudyVerbatimClient, TopicVerbatimWithTeamRef } from 'jf/api';
import { useClientQueries } from 'jf/utils/useClientQuery';

import { useSequenceStudies } from './useSequenceStudies';

type UseSequenceVerbatimsProps = {
  sequenceRef: string | undefined;
  teamRef?: string;
};

export const useSequenceVerbatims = (props: UseSequenceVerbatimsProps) => {
  const closedStudies = useSequenceStudies(props.sequenceRef, Study.status.CLOSED);

  const sequenceVerbatimsQueries = useClientQueries(
    StudyVerbatimClient.getTopicVerbatims,
    closedStudies?.map((study) => ({ studyRef: study.ref })) ?? [],
    { enabled: !props.teamRef }
  );

  const sequenceVerbatimsQueriesForTeam = useClientQueries(
    StudyVerbatimClient.getTopicVerbatimsForTeam,
    closedStudies?.map((study) => ({ studyRef: study.ref, teamRef: props.teamRef! })) ?? [],
    { enabled: !!props.teamRef }
  );

  const verbatimsMapping: { [ref: string]: TopicVerbatimWithTeamRef[] } = useMemo(() => {
    const queries = props.teamRef ? sequenceVerbatimsQueriesForTeam : sequenceVerbatimsQueries;
    if (!closedStudies || !queries) {
      return {};
    }
    return queries.reduce(
      (acc, verbatims, index) => ({
        ...acc,
        [closedStudies[index].ref]: verbatims.data!,
      }),
      {} as { [ref: string]: TopicVerbatimWithTeamRef[] }
    );
  }, [sequenceVerbatimsQueries, sequenceVerbatimsQueriesForTeam, closedStudies, props.teamRef]);

  return {
    sequenceVerbatimsQueries: props.teamRef
      ? sequenceVerbatimsQueriesForTeam
      : sequenceVerbatimsQueries,
    verbatims: verbatimsMapping,
  };
};
